/**
 * ScenarioSelector — Global scenario picker for Tab1Financials.
 *
 * Provides a horizontal pill/tab bar with 4 deal scenarios.
 * The BEST badge highlights whichever scenario delivers the highest cumulative return.
 *
 * Phase 180: Global Scenario Selector
 */

export type ScenarioId = 'std-clean' | 'obbb-clean' | 'std-1031' | 'obbb-1031';

export const SCENARIO_OPTIONS: { id: ScenarioId; label: string; shortLabel: string }[] = [
  { id: 'std-clean', label: 'Standard + Clean Exit', shortLabel: 'Std+Clean' },
  { id: 'obbb-clean', label: 'OBBB Bonus + Clean Exit', shortLabel: 'OBBB+Clean' },
  { id: 'std-1031', label: 'Standard + 1031 Exchange', shortLabel: 'Std+1031' },
  { id: 'obbb-1031', label: 'OBBB Bonus + 1031 Exchange', shortLabel: 'OBBB+1031' },
];

/**
 * Map a best_scenario_label string (from the backend FourScenarioComparison)
 * to a ScenarioId by checking for 'OBBB' and '1031' substrings.
 */
export function labelToScenarioId(label: string | undefined | null): ScenarioId {
  if (!label) return 'obbb-1031'; // most tax-advantaged default
  const upper = label.toUpperCase();
  const hasOBBB = upper.includes('OBBB');
  const has1031 = upper.includes('1031');
  if (hasOBBB && has1031) return 'obbb-1031';
  if (hasOBBB) return 'obbb-clean';
  if (has1031) return 'std-1031';
  return 'std-clean';
}

interface ScenarioSelectorProps {
  selected: ScenarioId;
  onChange: (id: ScenarioId) => void;
  bestScenario?: ScenarioId;
  className?: string;
}

export function ScenarioSelector({ selected, onChange, bestScenario, className }: ScenarioSelectorProps) {
  return (
    <div className={`flex gap-1 bg-charcoal/40 p-1 rounded-lg border border-slate-dark ${className ?? ''}`}>
      {SCENARIO_OPTIONS.map(opt => (
        <button
          key={opt.id}
          onClick={() => onChange(opt.id)}
          className={`flex-1 px-3 py-2 text-sm font-medium rounded-md transition-all flex items-center justify-center gap-1.5 ${
            selected === opt.id
              ? 'bg-gold/20 text-gold border border-gold/30'
              : 'text-stone-700 hover:text-ivory hover:bg-charcoal/60'
          }`}
        >
          {/* Short label on small screens, full label on lg */}
          <span className="lg:hidden">{opt.shortLabel}</span>
          <span className="hidden lg:inline">{opt.label}</span>
          {bestScenario === opt.id && (
            <span className="text-sm uppercase tracking-wider font-bold text-gold bg-gold/15 px-1.5 py-0.5 rounded">
              Best
            </span>
          )}
        </button>
      ))}
    </div>
  );
}
